"use client";
import { Track } from "@/src/lib/tracking";

import { useEffect, useRef } from "react";
import { useSnapshot } from "valtio";

import { getBridge } from "@/src/lib/bridge/bridge";
import { useBridge } from "@/src/lib/bridge/useBridge";
import {
  syncAdjustAdidFromBridge,
  syncOsAdvertisingIdFromBridge,
  syncOsIdfaFromBridge,
} from "@/src/lib/bridge/adjustRequestHeaders";
import { LoginStore } from "@store/modules/login";
import { UserStore } from "@store/modules/user";

/**
 * App WebView 内：同步 Adjust ADID 到公共请求头、登录后设置 external device id、
 * 启动时拉取 attribution 便于控制台确认字段。
 */
export function useAdjustNativeIntegration() {
  const { isInApp, adjustGetAttribution, adjustSetExternalDeviceId } =
    useBridge();
  const { isLoggedIn } = useSnapshot(LoginStore);
  const { ownerInfo } = useSnapshot(UserStore);

  const attributionLoggedRef = useRef(false);
  const lastExternalIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isInApp) {
      return;
    }
    const b = getBridge();
    void Promise.all([
      syncAdjustAdidFromBridge(b),
      syncOsAdvertisingIdFromBridge(b),
      syncOsIdfaFromBridge(b),
    ]);
  }, [isInApp]);

  useEffect(() => {
    if (!isInApp || attributionLoggedRef.current) {
      return;
    }
    attributionLoggedRef.current = true;
    void adjustGetAttribution()
      .then((data) => {
        Track.log("info", "adjust-get-attribution", "adjust-get-attribution", {
          cause: String(JSON.stringify(data, null, 2)),
        });
      })
      .catch((err) => {
        Track.log("warn", "adjust-get-attribution", "failed", {
          cause: String(err),
        });
      });
  }, [adjustGetAttribution, isInApp]);

  useEffect(() => {
    if (!isLoggedIn) {
      lastExternalIdRef.current = null;
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isInApp || !isLoggedIn || !ownerInfo?.id) {
      return;
    }
    const id = String(ownerInfo.id);
    if (lastExternalIdRef.current === id) {
      return;
    }
    lastExternalIdRef.current = id;
    void adjustSetExternalDeviceId(id).catch((err) => {
      Track.log("warn", "adjust-set-external-device-id", "failed", {
        cause: String(err),
      });
    });
  }, [adjustSetExternalDeviceId, isInApp, isLoggedIn, ownerInfo?.id]);
}
